/**
 * Subscriptions Page
 *
 * Create and manage webhook subscriptions.
 * Never displays signing secrets.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/api';

import { RequestPanel, StatusBadge } from '../components/RequestPanel';
import { Code } from '../components/JsonViewer';
import type { ApiResult, WebhookSubscription, SubscriptionListResponse } from '../lib/types';

const EVENT_TYPES = [
  'assessment.created',
  'artifact.committed',
  'transcript.generated',
  'score.generated',
];

export function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState<WebhookSubscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ApiResult<SubscriptionListResponse> | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  // Create form state
  const [endpointUrl, setEndpointUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState<ApiResult<WebhookSubscription> | null>(null);
  const [newSigningSecret, setNewSigningSecret] = useState<string | null>(null);

  // Status toggle
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [toggleResult, setToggleResult] = useState<ApiResult<WebhookSubscription> | null>(null);

  // Detail view
  const [selectedSubscription, setSelectedSubscription] = useState<WebhookSubscription | null>(null);

  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    const res = await apiClient.listSubscriptions({ limit: 50 });
    setResult(res);
    if (res.response.data) {
      setSubscriptions(res.response.data.items);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!endpointUrl.trim() || selectedEvents.length === 0) return;

    // Validate HTTPS
    if (!endpointUrl.startsWith('https://')) {
      alert('Endpoint URL must use HTTPS');
      return;
    }

    setCreating(true);
    setCreateResult(null);
    setNewSigningSecret(null);

    const res = await apiClient.createSubscription({
      endpointUrl: endpointUrl.trim(),
      subscribedEventTypes: selectedEvents,
    });

    setCreateResult(res);
    setCreating(false);

    if (res.response.data) {
      // Capture signing secret (only returned on creation)
      if (res.response.data.signingSecret) {
        setNewSigningSecret(res.response.data.signingSecret);
      }
      await loadSubscriptions();
      setEndpointUrl('');
      setSelectedEvents([]);
    }
  };

  const handleToggleStatus = async (sub: WebhookSubscription) => {
    const newStatus = sub.status === 'active' ? 'paused' : 'active';
    setTogglingId(sub.id);
    setToggleResult(null);

    const res = await apiClient.updateSubscriptionStatus(sub.id, newStatus);
    setToggleResult(res);
    setTogglingId(null);

    if (res.response.data) {
      await loadSubscriptions();
    }
  };

  const toggleEvent = (eventType: string) => {
    setSelectedEvents((prev) =>
      prev.includes(eventType)
        ? prev.filter((e) => e !== eventType)
        : [...prev, eventType]
    );
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 30, fontWeight: 600, letterSpacing: '-0.025em', color: 'var(--slate-900)', marginBottom: 4 }}>
            Subscriptions
          </h1>
          <p style={{ color: 'var(--slate-500)', fontSize: 14, margin: 0 }}>
            Manage webhook subscriptions for event delivery
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-tertiary" onClick={loadSubscriptions} disabled={loading}>
            <iconify-icon icon="lucide:refresh-cw" width="16" height="16"></iconify-icon>
            Refresh
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setShowCreate(!showCreate);
              setCreateResult(null);
              setNewSigningSecret(null);
            }}
          >
            {showCreate ? (
              <>
                <iconify-icon icon="lucide:x" width="16" height="16"></iconify-icon>
                Cancel
              </>
            ) : (
              <>
                <iconify-icon icon="lucide:plus" width="16" height="16"></iconify-icon>
                Create Subscription
              </>
            )}
          </button>
        </div>
      </div>

      {showCreate && (
        <div className="card">
          <h2>Create Subscription</h2>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label className="label">Endpoint URL (HTTPS required) *</label>
              <input
                type="url"
                className="input mono"
                value={endpointUrl}
                onChange={(e) => setEndpointUrl(e.target.value)}
                placeholder="https://your-endpoint.com/webhook"
                required
              />
            </div>

            <div className="form-group">
              <label className="label">Event Types *</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {EVENT_TYPES.map((eventType) => (
                  <label
                    key={eventType}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(eventType)}
                      onChange={() => toggleEvent(eventType)}
                    />
                    <code className="mono" style={{ fontSize: 13 }}>
                      {eventType}
                    </code>
                  </label>
                ))}
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={creating || !endpointUrl.trim() || selectedEvents.length === 0}
            >
              {creating ? 'Creating...' : 'Create'}
            </button>
          </form>

          {/* Show signing secret warning */}
          {newSigningSecret && (
            <div
              className="success-box"
              style={{ marginTop: 16 }}
            >
              <strong>Subscription Created!</strong>
              <p style={{ margin: '8px 0' }}>
                Save the signing secret below. It will NOT be shown again.
              </p>
              <div
                style={{
                  background: '#166534',
                  color: '#fff',
                  padding: '8px 12px',
                  borderRadius: 4,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                {newSigningSecret}
              </div>
            </div>
          )}

          <RequestPanel result={createResult} loading={creating} />
        </div>
      )}

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Endpoint</th>
              <th>Events</th>
              <th>Status</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscriptions.map((sub) => (
              <tr key={sub.id}>
                <td>
                  <Code>{sub.id.slice(0, 12)}...</Code>
                </td>
                <td
                  className="mono text-small"
                  style={{
                    maxWidth: 300,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={sub.endpointUrl}
                >
                  {sub.endpointUrl}
                </td>
                <td className="text-small">
                  {sub.subscribedEventTypes.length} event(s)
                </td>
                <td>
                  <StatusBadge status={sub.status} />
                </td>
                <td className="text-muted">{new Date(sub.createdAt).toLocaleString()}</td>
                <td>
                  <div className="flex gap-2">
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => setSelectedSubscription(sub)}
                    >
                      Details
                    </button>
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '4px 8px', fontSize: 12 }}
                      onClick={() => handleToggleStatus(sub)}
                      disabled={togglingId === sub.id}
                    >
                      {togglingId === sub.id
                        ? '...'
                        : sub.status === 'active'
                          ? 'Pause'
                          : 'Activate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {subscriptions.length === 0 && !loading && (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: 48 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <iconify-icon icon="lucide:webhook" width="32" height="32" style={{ color: 'var(--slate-300)' }}></iconify-icon>
                    <span style={{ color: 'var(--slate-400)' }}>No subscriptions found</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <RequestPanel result={result} loading={loading} />
      {toggleResult && <RequestPanel result={toggleResult} />}

      {/* Subscription Detail Modal */}
      {selectedSubscription && (
        <div className="card">
          <div className="flex justify-between items-center">
            <h2>Subscription Detail</h2>
            <button
              className="btn btn-secondary"
              style={{ padding: '4px 8px', fontSize: 12 }}
              onClick={() => setSelectedSubscription(null)}
            >
              Close
            </button>
          </div>

          <table className="table" style={{ maxWidth: 600 }}>
            <tbody>
              <tr>
                <td style={{ fontWeight: 500, width: 150 }}>ID</td>
                <td><Code>{selectedSubscription.id}</Code></td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Status</td>
                <td><StatusBadge status={selectedSubscription.status} /></td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Endpoint URL</td>
                <td className="mono" style={{ wordBreak: 'break-all' }}>
                  {selectedSubscription.endpointUrl}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Event Types</td>
                <td>
                  {selectedSubscription.subscribedEventTypes.map((e) => (
                    <div key={e}>
                      <Code>{e}</Code>
                    </div>
                  ))}
                </td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Signing Secret</td>
                <td className="text-muted">(never displayed)</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Created At</td>
                <td>{new Date(selectedSubscription.createdAt).toLocaleString()}</td>
              </tr>
              <tr>
                <td style={{ fontWeight: 500 }}>Updated At</td>
                <td>{new Date(selectedSubscription.updatedAt).toLocaleString()}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: 16 }}>
            <a
              href={`/console/deliveries?subscriptionId=${selectedSubscription.id}`}
              className="btn btn-primary"
            >
              View Deliveries →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
